import { FLOATING_NUMBER_FADE_START } from "#/game/animation/constants";
import type {
  CanvasAttackFxAnimation,
  CanvasBoardAnimation,
  CanvasFloatingNumber,
  CanvasRepairFxAnimation,
} from "#/game/animation/types";

import { attackFxCoreProgress } from "./boardAnimations";
import { TILE_SIZE } from "./boardConstants";
import { drawAttackFxUnderUnits } from "./boardFxCombat";

const FLOATING_NUMBER_COLORS: Record<CanvasFloatingNumber["kind"], string> = {
  damage: "#fca5a5",
  repair: "#86efac",
};

function floatingNumberMotion(t: number): {
  yOffset: number;
  fade: number;
  pop: number;
} {
  return {
    yOffset: -8 - t * 22,
    fade:
      t < FLOATING_NUMBER_FADE_START
        ? 1
        : Math.max(0, 1 - (t - FLOATING_NUMBER_FADE_START) / (1 - FLOATING_NUMBER_FADE_START)),
    pop: t < 0.18 ? 0.6 + (t / 0.18) * 0.55 : 1.15 - Math.min(0.15, (t - 0.18) * 0.4),
  };
}

function strokeFloatingNumber({
  ctx,
  n,
  motion,
}: {
  ctx: CanvasRenderingContext2D;
  n: CanvasFloatingNumber;
  motion: { yOffset: number; fade: number; pop: number };
}): void {
  const cx = n.tile.x * TILE_SIZE + TILE_SIZE / 2;
  const cy = n.tile.y * TILE_SIZE + TILE_SIZE / 2 + motion.yOffset;
  const sign = n.kind === "damage" ? "-" : "+";
  const text = `${sign}${n.percent}%`;
  ctx.save();
  ctx.globalAlpha = motion.fade;
  ctx.translate(cx, cy);
  ctx.scale(motion.pop, motion.pop);
  ctx.lineWidth = 3.5;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.9)";
  ctx.strokeText(text, 0, 0);
  ctx.fillStyle = FLOATING_NUMBER_COLORS[n.kind];
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function drawFloatingNumbers({
  ctx,
  numbers,
  t,
}: {
  ctx: CanvasRenderingContext2D;
  numbers: CanvasFloatingNumber[];
  t: number;
}): void {
  if (numbers.length === 0) return;
  const motion = floatingNumberMotion(t);
  ctx.save();
  ctx.font = "bold 16px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  for (const n of numbers) strokeFloatingNumber({ ctx, n, motion });
  ctx.restore();
}

function drawAttackFxOverUnits({
  ctx,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  animation: CanvasAttackFxAnimation;
  nowMs: number;
}): void {
  const coreT = attackFxCoreProgress(animation, nowMs);
  if (coreT < 0) return;
  drawFloatingNumbers({ ctx, numbers: animation.damageNumbers, t: coreT });
}

function drawRepairFx({
  ctx,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  animation: CanvasRepairFxAnimation;
  nowMs: number;
}): void {
  const elapsed = Math.max(0, nowMs - animation.startedAtMs);
  const t = Math.min(1, elapsed / Math.max(1, animation.durationMs));
  drawFloatingNumbers({ ctx, numbers: animation.numbers, t });
}

export function drawFxUnderUnitsLayer({
  ctx,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): void {
  if (animation?.type === "attackFx") {
    drawAttackFxUnderUnits({ ctx, animation, nowMs });
  }
}

export function drawFxOverUnitsLayer({
  ctx,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): void {
  if (animation?.type === "attackFx") {
    drawAttackFxOverUnits({ ctx, animation, nowMs });
  } else if (animation?.type === "repairFx") {
    drawRepairFx({ ctx, animation, nowMs });
  }
}
